import { FocusEventHandler, useEffect, useRef, useState } from 'react';
import { useDebounce, useFormInput } from '@/hooks';
import { Error, Group, Input as BaseInput, Label } from '@/Components/UI/Form/Partials';
import { useTranslation } from 'react-i18next';
import { FormInputProps } from '@/types';

type Suggestion = {
    value: string;
    label: string;
}

type InputProps = FormInputProps<HTMLInputElement, {
    getSuggestions: (v: string) => Promise<Suggestion[] | undefined>
}>

export const AutocompleteInput = ({
                                      id,
                                      name,
                                      label,
                                      required = false,
                                      disabled = false,
                                      groupClassName,
                                      getSuggestions,
                                      ...rest
                                  }: InputProps) => {
    const { t } = useTranslation('misc');

    const { value, setValue, error } = useFormInput<string>(name);
    const containerRef = useRef<HTMLDivElement>(null);

    const [showSuggestions, setShowSuggestions] = useState(false);
    const [suggestions, setSuggestions] = useState<Suggestion[]>();
    const throttledValue = useDebounce(value, 500);

    useEffect(() => {
        if (!throttledValue || throttledValue.length < 3) {
            setSuggestions([]);
            return;
        }

        getSuggestions(throttledValue)
            .then(response => setSuggestions(response || []))
            .catch(() => setSuggestions([]));
    }, [throttledValue]);

    const onWrapperBlur: FocusEventHandler<HTMLDivElement> = (e) => {
        if (
            e.relatedTarget == null || !(containerRef.current?.contains(e.relatedTarget))
        ) {
            setShowSuggestions(false);
        }
    };

    const onSuggestionClick = (s: Suggestion) => {
        setValue(s.value);
        setShowSuggestions(false);
    };

    return (
        <Group className={groupClassName}>
            <Label
                htmlFor={id ?? name}
                required={required}
                disabled={disabled}
                invalid={error !== undefined}>
                {label}
            </Label>

            <div
                ref={containerRef}
                className="relative"
                onBlur={onWrapperBlur}
            >
                <BaseInput
                    {...rest}
                    id={id ?? name}
                    name={name}
                    value={value ?? ''}
                    disabled={disabled}
                    onFocus={() => setShowSuggestions(true)}
                    onChange={(e) => setValue(e.target.value)}
                    invalid={error !== undefined} />

                {showSuggestions && suggestions && (
                    <div
                        className={'absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-lg border border-surface-3 bg-surface-3 shadow-lg'}>
                        {suggestions.length > 0 ? (
                            suggestions.map((suggestion) => (
                                <button
                                    key={suggestion.value}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => onSuggestionClick(suggestion)}
                                    className={'block w-full p-2 text-start hover:bg-surface-5'}
                                >
                                    {suggestion.label}
                                </button>
                            ))
                        ) : (
                            <div className={'p-2 text-start text-sm text-on-surface/50'}>
                                {t('noSuggestions')}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <Error error={error} />
        </Group>
    );
};
